/**
 * Local visit state (Phase 16C C2, design §6.2 / §6.7). Encrypted store `visits`, key = visitId
 * (a client ULID for offline starts, the server id for visits cached while online).
 */
import type { OfflineRuntime } from '../runtime';

export type VisitLifecycle = 'IN_PROGRESS' | 'ENDED' | 'CANCELLED';
export type SyncState = 'pending' | 'synced' | 'conflict' | 'needs_attention';

/** The plan's status names, derived from lifecycle + sync state. */
export type LocalVisitStatus = 'LOCAL_IN_PROGRESS' | 'LOCAL_ENDED' | 'LOCAL_CANCELLED' | 'SYNCED';

export interface VisitCoords {
  latitude: number;
  longitude: number;
}

export interface LocalVisit {
  visitId: string;
  patientId: string;
  scheduleId?: string;
  lifecycle: VisitLifecycle;
  syncState: SyncState;
  /** 'local' = started on this device; 'server' = cached from the server */
  origin: 'local' | 'server';
  startedAt: string;
  endedAt?: string;
  coords?: VisitCoords | null;
  gpsDenied?: boolean;
  discipline?: string;
  visitType?: string;
  serviceType?: string;
  authorizationId?: string;
  evvRequired?: boolean;
  chartingStatus: string;
  signatureBlobId?: string;
  serverVersion?: number;
  syncFlags?: string[];
  /** For the "In visit" bar while offline (encrypted like everything else) */
  patientName?: string;
  updatedAt: string;
}

export function localStatus(v: LocalVisit): LocalVisitStatus {
  if (v.syncState === 'synced') return 'SYNCED';
  if (v.lifecycle === 'CANCELLED') return 'LOCAL_CANCELLED';
  return v.lifecycle === 'ENDED' ? 'LOCAL_ENDED' : 'LOCAL_IN_PROGRESS';
}

export interface StartLocalInput {
  visitId: string;
  patientId: string;
  startedAt: string;
  scheduleId?: string;
  coords?: VisitCoords | null;
  gpsDenied?: boolean;
  discipline?: string;
  visitType?: string;
  serviceType?: string;
  authorizationId?: string;
  evvRequired?: boolean;
}

export class ActiveVisitExistsError extends Error {
  constructor(readonly activeVisitId: string) {
    super('Another visit is already in progress on this device. End it before starting a new one.');
    this.name = 'ActiveVisitExistsError';
  }
}

export class VisitRepo {
  constructor(private readonly rt: OfflineRuntime) {}

  private stamp(): string { return new Date(this.rt.now()).toISOString(); }

  async get(visitId: string): Promise<LocalVisit | undefined> {
    return this.rt.store().get<LocalVisit>('visits', visitId);
  }

  async put(visit: LocalVisit): Promise<LocalVisit> {
    const next = { ...visit, updatedAt: this.stamp() };
    await this.rt.store().put('visits', visit.visitId, next);
    return next;
  }

  async list(): Promise<LocalVisit[]> {
    return (await this.rt.store().getAll<LocalVisit>('visits')).map((r) => r.value);
  }

  /** The visit in progress for this user (local first; one at a time). */
  async getActive(): Promise<LocalVisit | null> {
    const active = (await this.list()).filter((v) => v.lifecycle === 'IN_PROGRESS');
    return active.sort((a, b) => (a.origin === b.origin ? b.startedAt.localeCompare(a.startedAt) : a.origin === 'local' ? -1 : 1))[0] || null;
  }

  async startLocal(input: StartLocalInput): Promise<LocalVisit> {
    const active = await this.getActive();
    if (active && active.visitId !== input.visitId) throw new ActiveVisitExistsError(active.visitId);
    return this.put({
      ...input,
      lifecycle: 'IN_PROGRESS',
      syncState: 'pending',
      origin: 'local',
      chartingStatus: 'DRAFT',
      updatedAt: '',
    });
  }

  async endLocal(visitId: string, endedAt: string, signatureBlobId?: string): Promise<LocalVisit> {
    const visit = await this.get(visitId);
    if (!visit) throw new Error('Visit not found on this device');
    return this.put({ ...visit, lifecycle: 'ENDED', endedAt, signatureBlobId, syncState: 'pending' });
  }

  async cancelLocal(visitId: string): Promise<LocalVisit> {
    const visit = await this.get(visitId);
    if (!visit) throw new Error('Visit not found on this device');
    return this.put({ ...visit, lifecycle: 'CANCELLED', syncState: 'pending' });
  }

  async setChartingStatus(visitId: string, chartingStatus: string, syncState: SyncState = 'pending'): Promise<void> {
    const visit = await this.get(visitId);
    if (visit) await this.put({ ...visit, chartingStatus, syncState: visit.syncState === 'synced' ? syncState : visit.syncState });
  }

  /** A server visit seen while online (active visit, post-visit packet). Never overwrites local changes. */
  async cacheServer(input: Omit<LocalVisit, 'origin' | 'syncState' | 'updatedAt'>): Promise<LocalVisit> {
    const existing = await this.get(input.visitId);
    if (existing && existing.syncState !== 'synced') return existing;
    return this.put({ ...existing, ...input, origin: existing?.origin || 'server', syncState: 'synced', updatedAt: '' });
  }

  async markSynced(visitId: string, patch: Partial<LocalVisit> = {}): Promise<void> {
    const visit = await this.get(visitId);
    if (visit) await this.put({ ...visit, ...patch, syncState: 'synced' });
  }

  async delete(visitId: string): Promise<void> {
    await this.rt.store().delete('visits', visitId);
  }
}
